import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MapPin, Users, Calendar, ChevronRight, AlertTriangle, Globe, Link as LinkIcon, Youtube, Lock, Mail, Instagram, Facebook } from 'lucide-react';
import { type ExternalLinkType, clubsApi } from '../lib/api/clubs';
import { ApiError } from '../lib/api';
import { useClub, useJoinClub, useLeaveClub } from '../hooks/useClubs';
import { useClubMembers } from '../hooks/useMembers';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { TopBar, EventCardSkeleton, ListItemSkeleton, HeroImageUpload, ConfirmDialog, EmptyState } from '../components';
import { Skeleton } from '../components/ui/Skeleton';
import ClubMemberInviteModal from '../components/ClubMemberInviteModal';
import { toast } from 'sonner';
import { countLabel } from '../lib/formatters';



export default function ClubDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if accessed from Home tab (Issue #692)
  const fromHomeTab = location.state?.fromHomeTab === true;
  
  // React Query hooks
  const { data: club, isLoading: loading, error: queryError } = useClub(slug || '');
  const joinClubMutation = useJoinClub();
  const leaveClubMutation = useLeaveClub();
  
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const queryClient = useQueryClient();

  // Determine membership status
  const { club: clubData, stats, membership, external_links } = club || { club: null, stats: null, membership: null, external_links: [] };

  // Set page title for browser history (#896)
  useDocumentTitle(clubData?.name);
  const isMember = membership?.role !== 'none';
  const isLeader = membership?.role === 'leader' || membership?.is_owner;
  const canManageClub = isLeader;
  // #912: Members can invite when club allows all_members to invite.
  // Fail CLOSED on 'disabled': the API rejects invite creation for EVERYONE —
  // leaders/owners included — when invite_permission is 'disabled'
  // (invites.ts:604). The client predicate must not offer the affordance
  // either, or the UI hint and the API enforcement disagree (Core Rule 48).
  // #2642 review round 3.
  const canInvite =
    clubData?.invite_permission !== 'disabled' &&
    (isLeader || (isMember && clubData?.invite_permission === 'all_members'));

  // External link icon helper (#758, #1109, #1184 removed strava, #1800 Lucide icons for IG/FB)
  const getLinkIcon = (type: ExternalLinkType) => {
    switch (type) {
      case 'website': return <Globe className="w-4 h-4" />;
      case 'instagram': return <Instagram className="w-4 h-4 text-brand-instagram" />;
      case 'facebook': return <Facebook className="w-4 h-4 text-brand-facebook" />;
      case 'youtube': return <Youtube className="w-4 h-4 text-brand-youtube" />;
      case 'custom': return <LinkIcon className="w-4 h-4" />;
    }
  };

  const getLinkLabel = (type: ExternalLinkType, label?: string | null) => {
    if (label) return label;
    switch (type) {
      case 'website': return 'Website';
      case 'instagram': return 'Instagram';
      case 'facebook': return 'Facebook Group';
      case 'youtube': return 'YouTube';
      case 'custom': return 'Link';
    }
  };

  // Handle hero image upload (#783, #816)
  const handleHeroImageUpload = async (file: File): Promise<string> => {
    if (!clubData?.id) throw new Error('Club not loaded');

    const result = await clubsApi.uploadClubHeroImage(clubData.id, file);
    toast.success('Cover photo updated');
    queryClient.invalidateQueries({ queryKey: ['club', slug] });
    return result.image_url;
  };

  const handleHeroImageRemove = async () => {
    if (!clubData?.id) return;

    await clubsApi.removeClubHeroImage(clubData.id);
    toast.success('Cover photo removed');
    queryClient.invalidateQueries({ queryKey: ['club', slug] });
  };

  // Fetch club members (for preview)
  const { data: clubMembers } = useClubMembers(slug || '');

  const handleJoin = async () => {
    if (!slug) return;
    
    try {
      await joinClubMutation.mutateAsync(slug);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to join club');
    }
  };

  const handleLeaveClick = () => {
    setShowLeaveConfirm(true);
  };

  const handleLeaveConfirm = async () => {
    if (!slug) return;
    setShowLeaveConfirm(false);

    try {
      await leaveClubMutation.mutateAsync(slug);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to leave club');
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar 
          title="Club" 
          onBack={fromHomeTab ? undefined : () => navigate(-1)} 
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-6">
            {/* Hero image skeleton */}
            <Skeleton variant="image" className="h-48 w-full rounded-2xl" />
            
            {/* Club name and description */}
            <div className="space-y-3">
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="text-center space-y-2">
                  <Skeleton className="h-6 w-12 mx-auto" />
                  <Skeleton className="h-3 w-16 mx-auto" />
                </div>
              ))}
            </div>
            
            {/* Join button skeleton */}
            <Skeleton className="h-12 w-full rounded-lg" />
            
            {/* Upcoming events section */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-40" />
              <div className="flex gap-3 overflow-x-auto">
                {[1, 2].map((i) => (
                  <EventCardSkeleton key={i} />
                ))}
              </div>
            </div>
            
            {/* Members section */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              {[1, 2, 3].map((i) => (
                <ListItemSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  const displayError = error || (queryError instanceof Error ? queryError.message : null);
  if (displayError && !clubData) {
    // A missing/invalid slug is the dominant case (404). Show the standard
    // not-found EmptyState with a Browse-clubs doorway (F1: dead ends become
    // doors). Non-404 errors keep the raw message so nothing is lost. The
    // wrapper mirrors the loading/success branches — min-h-screen + a
    // pt-14 scroll container — so the card clears the fixed TopBar (#3057).
    const isNotFound = queryError instanceof ApiError && queryError.status === 404;
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar
          title="Club"
          onBack={fromHomeTab ? undefined : () => navigate(-1)}
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              variant="default"
              icon={<Users className="w-12 h-12" />}
              title={isNotFound ? 'Club not found' : 'Something went wrong'}
              message={
                isNotFound
                  ? 'This club may have been removed, or the link may be incorrect.'
                  : displayError
              }
              actionLabel="Browse clubs"
              onAction={() => navigate('/clubs')}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!clubData) {
    return null;
  }

  // Check if any mutation is in progress
  const actionLoading = joinClubMutation.isPending || leaveClubMutation.isPending;
  
  // Member preview (first 3)
  const memberPreview = clubMembers?.members?.slice(0, 3) || [];
  const remainingMembers = (stats?.member_count || 0) - memberPreview.length;

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar
        title={fromHomeTab ? undefined : (clubData?.name || "Club")}
        onBack={fromHomeTab ? undefined : () => navigate(-1)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Hero Image - First Content Block (#783, #816: Using HeroImageUpload) */}
          {canManageClub ? (
            <HeroImageUpload
              currentImageUrl={clubData?.hero_image_url}
              onImageChange={() => {
                // Changes are handled by invalidating query in upload/remove handlers
              }}
              onImageUpload={handleHeroImageUpload}
              onImageRemove={handleHeroImageRemove}
              enableCrop={true}
              compact={true}
              className="mb-4"
            />
          ) : clubData?.hero_image_url ? (
            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-card mb-4">
              <img
                src={clubData.hero_image_url}
                alt={clubData.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent" />
            </div>
          ) : null}

          {/* Club Identity Card */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
            <div className="flex items-start gap-4">
              {/* Avatar with accent ring (#1799: avatar_image_url, not hero) */}
              <div className="relative shrink-0">
                <div className="w-16 h-16 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden ring-2 ring-accent/30">
                  {clubData?.avatar_image_url ? (
                    <img
                      src={clubData.avatar_image_url}
                      alt={`${clubData.name} avatar`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Users className="w-8 h-8 text-accent" />
                  )}
                </div>
              </div>

              {/* Club Info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-text-primary font-bold text-lg">{clubData?.name}</h2>
                {(clubData?.city || clubData?.state || clubData?.location) && (
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-text-muted" />
                    <span className="text-text-secondary text-sm">
                      {clubData.location || `${clubData.city || ''}${clubData.city && clubData.state ? ', ' : ''}${clubData.state || ''}`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Users className="w-4 h-4 text-text-muted" />
                  <span className="text-text-muted text-sm">
                    {countLabel(stats?.member_count || 0, 'member')}
                  </span>
                </div>

                {/* Privacy Badge - Only show for members (below meta so title gets full width) */}
                {isMember && (
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs font-medium rounded-full ${
                      clubData?.visibility === 'public'
                        ? 'bg-oro-success/10 text-oro-success border border-oro-success/20'
                        : clubData?.visibility === 'private'
                        ? 'bg-oro-error/10 text-oro-error border border-oro-error/20'
                        : 'bg-oro-warning/10 text-oro-warning border border-oro-warning/20'
                    }`}>
                      {clubData?.visibility === 'public' ? <><Globe className="w-3 h-3" /> Public</> :
                       clubData?.visibility === 'private' ? <><Lock className="w-3 h-3" /> Private</> :
                       <><Mail className="w-3 h-3" /> Invite Only</>}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Join Button - Only shown if not a member */}
            {!isMember && (
              <button
                onClick={handleJoin}
                disabled={actionLoading}
                className="w-full mt-4 px-4 py-3 rounded-lg font-medium transition-all bg-accent hover:bg-accent text-black shadow-glow"
              >
                {actionLoading ? 'Loading...' : 'Join Club'}
              </button>
            )}

            {/* Leadership Controls - Only for leaders and owners */}
            {canManageClub && (
              <div className="mt-4 pt-4 border-t border-oro-border space-y-2">
                <button
                  onClick={() => navigate(`/clubs/${slug}/events/create`)}
                  className="w-full px-4 py-3 bg-oro-gold hover:bg-oro-gold text-black font-medium rounded-lg transition-all shadow-card"
                >
                  Create Event
                </button>
                <button
                  onClick={() => navigate(`/clubs/${slug}/events/manage`)}
                  className="w-full px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                >
                  Manage Events
                </button>
                <div className="grid grid-cols-2 gap-2">
                  {/* Invite is hidden when invite_permission is 'disabled' — the
                      API rejects it for leaders too (invites.ts:604), #2642. */}
                  {canInvite && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                    >
                      Invite Members
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/clubs/${slug}/settings`)}
                    className="px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                  >
                    Club Settings
                  </button>
                </div>
                {membership?.is_owner && (
                  <p className="text-xs text-oro-gold text-center mt-2 font-medium">
                    Club Owner
                  </p>
                )}
                {membership?.role === 'leader' && !membership?.is_owner && (
                  <p className="text-xs text-accent text-center mt-2 font-medium">
                    Club Leader
                  </p>
                )}
              </div>
            )}

            {/* Member Invite Button - shown when members can invite but aren't leaders (#912) */}
            {canInvite && !canManageClub && (
              <div className="mt-4 pt-4 border-t border-oro-border">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="w-full px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                >
                  Invite Members
                </button>
              </div>
            )}
          </div>

          {/* Description Card */}
          {clubData?.description && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
              <h3 className="text-text-primary font-semibold mb-2">About</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                {clubData.description}
              </p>
            </div>
          )}

          {/* External Links (#758) */}
          {external_links && external_links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {external_links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-oro-surface border border-oro-border rounded-lg text-sm text-text-secondary hover:text-text-primary hover:border-accent/50 transition-colors"
                >
                  {getLinkIcon(link.type)}
                  <span>{getLinkLabel(link.type, link.label)}</span>
                </a>
              ))}
            </div>
          )}

          {/* Sports & Activities */}
          {(clubData?.primary_sport || (clubData?.secondary_sports && clubData.secondary_sports.length > 0)) && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
              <h3 className="text-text-primary font-semibold mb-3">Sports & Activities</h3>
              <div className="flex flex-wrap gap-2">
                {clubData.primary_sport && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 text-accent border border-accent/20 rounded-full">
                    <span className="text-sm font-medium">{clubData.primary_sport}</span>
                    <span className="px-1.5 py-0.5 bg-accent/20 text-xs font-bold rounded">PRIMARY</span>
                  </div>
                )}
                {clubData.secondary_sports?.map((sport, index) => (
                  <div key={index} className="flex items-center gap-1 px-3 py-1.5 bg-oro-surface-alt text-text-secondary border border-oro-border rounded-full">
                    <span className="text-sm">{sport}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members Preview - Only for members */}
          {isMember && memberPreview.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-text-primary font-semibold">Members</h3>
                <button
                  onClick={() => navigate(`/clubs/${slug}/members`)}
                  className="text-accent hover:text-accent text-sm transition-colors"
                >
                  View All
                </button>
              </div>
              <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
                <div className="flex items-center gap-3">
                  {memberPreview.map((member) => (
                    <div key={member.user_id} className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden">
                        {member.profile_image_url ? (
                          <img
                            src={member.profile_image_url}
                            alt={member.public_display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-accent font-semibold">
                            {member.public_display_name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-text-muted mt-1 text-xs">
                        {member.public_display_name.split(' ')[0]}
                      </span>
                    </div>
                  ))}
                  {remainingMembers > 0 && (
                    <button
                      onClick={() => navigate(`/clubs/${slug}/members`)}
                      className="flex flex-col items-center"
                    >
                      <div className="w-12 h-12 rounded-full bg-oro-surface-alt border border-oro-border flex items-center justify-center">
                        <ChevronRight className="w-5 h-5 text-text-muted" />
                      </div>
                      <span className="text-text-muted mt-1 text-xs">
                        +{remainingMembers}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Club Chat removed - Issue #703 Phase 4 */}
          {/* Chat is now accessible via Discuss tab in bottom navigation */}

          {/* Upcoming Events - Placeholder */}
          {isMember && stats && stats.upcoming_event_count > 0 && (
            <div className="pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-text-primary font-semibold">Upcoming Events</h3>
                <button
                  onClick={() => navigate(`/clubs/${slug}/events`)}
                  className="text-accent hover:text-accent text-sm transition-colors"
                >
                  View All
                </button>
              </div>
              <button
                onClick={() => navigate(`/clubs/${slug}/events`)}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-xl p-4 shadow-card hover:shadow-card transition-all duration-200 text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-text-muted" />
                      <span className="text-text-secondary text-sm">
                        {stats.upcoming_event_count} upcoming {stats.upcoming_event_count === 1 ? 'event' : 'events'}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted" />
                </div>
              </button>
            </div>
          )}

          {/* Error Message */}
          {displayError && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 text-text-primary text-center text-sm">
              {displayError}
            </div>
          )}

          {/* Danger Zone - Leave Club (members only, not owners) */}
          {isMember && !membership?.is_owner && (
            <div className="mt-12 pt-8 border-t border-oro-border">
              <div className="bg-oro-error/5 border border-oro-error/20 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-oro-error mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </h3>
                <p className="text-sm text-text-muted mb-4">
                  Once you leave this club, you'll lose access to all events, chat history, and member benefits.
                  You'll need to be re-invited to join again.
                </p>
                <button
                  onClick={handleLeaveClick}
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-lg font-medium bg-oro-error/10 border border-oro-error/30 text-oro-error hover:bg-oro-error/20 hover:border-oro-error/50 active:bg-oro-error/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Leaving...' : 'Leave Club'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Club Member Invite Modal */}
      {club && (
        <ClubMemberInviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          clubId={club.club.id}
          clubName={club.club.name}
          isLeader={isLeader}
        />
      )}


      {/* Leave Club Confirmation Dialog (#1029) */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        title="Leave Club"
        message="Are you sure you want to leave this club? You can rejoin anytime if the club allows it."
        confirmLabel="Leave"
        cancelLabel="Stay"
        variant="destructive"
        isPending={leaveClubMutation.isPending}
        onConfirm={handleLeaveConfirm}
        onCancel={() => setShowLeaveConfirm(false)}
      />
    </div>
  );
}
